import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Paper, TableBody, TableCell, TableRow } from "@mui/material";
import styled from "@emotion/styled";

import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import { usePagination } from "Components/Common/BaseTable";
import Pagination from "Components/Common/BaseTable/Pagination";
import { StyledTable, StyledTableCell, StyledTableHead } from "./style";

const RECORDS_PER_PAGE = 10;

const StyledTableContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "maxHeight",
})`
  max-height: ${({ maxHeight }) => maxHeight ?? "unset"};
  overflow-y: auto;
  .MuiPaper-root {
    overflow-x: scroll;
  }
`;

const ActiveDisconnectCategoryOrTariffTable = ({
  data,
  keys,
  maxHeight,
  t,
}) => {
  const paginate = usePagination();
  const [parentKey, childKey, ...subChildKey] = keys;
  const [rowData, setRowData] = useState([]);
  const { page, setTotalPages } = paginate;

  const getRowData = (index) =>
    data?.[0]?.[parentKey]?.reduce((acc, item) => {
      const rowIndex = page > 1 ? (page - 1) * RECORDS_PER_PAGE + index : index;
      return [
        ...acc,
        item[childKey]?.[rowIndex]?.[subChildKey[1]],
        item[childKey]?.[rowIndex]?.[subChildKey[2]],
      ];
    }, []);

  const tableData = data?.[0]?.[parentKey];

  useEffect(() => {
    const totalRows = data?.[0]?.[parentKey]?.[0]?.[childKey] || [];

    if (totalRows.length) {
      setTotalPages(Math.ceil(totalRows.length / RECORDS_PER_PAGE));
    }

    const rowData = totalRows.slice(
      RECORDS_PER_PAGE * (page - 1),
      RECORDS_PER_PAGE * page
    );
    setRowData(rowData);
  }, [page, setTotalPages, data]);

  const tariffOrCategoryName = rowData?.map((item) => item?.[subChildKey[0]]);

  return (
    <>
      <StyledTableContainer maxHeight={maxHeight}>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell rowSpan={2}>
                {t(convertCamelTextToCapitalizeText(subChildKey[0]))}
              </StyledTableCell>
              {tableData?.map(({ month }, index) => (
                <React.Fragment key={index}>
                  <StyledTableCell colSpan={2} align="center">
                    {t(`${month}`)}
                  </StyledTableCell>
                </React.Fragment>
              ))}
            </TableRow>
            <TableRow>
              {tableData?.map((index) => (
                <React.Fragment key={index}>
                  <StyledTableCell>
                    {t("Activated During Month")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {t("Disconnected During Month")}
                  </StyledTableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </StyledTableHead>

          <TableBody>
            {tariffOrCategoryName?.length ? (
              tariffOrCategoryName?.map((item, index) => (
                <TableRow key={item}>
                  <TableCell>{item}</TableCell>
                  {getRowData(index)?.map((count, index) => (
                    <>
                      <TableCell key={index} align="center">
                        {count}
                      </TableCell>
                    </>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} className="text-center">
                  {t("No Data Available")}!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      {!!tariffOrCategoryName?.length && <Pagination paginate={paginate} />}
    </>
  );
};

ActiveDisconnectCategoryOrTariffTable.propTypes = {
  data: PropTypes.array,
  keys: PropTypes.array,
  maxHeight: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(ActiveDisconnectCategoryOrTariffTable);
